import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Briefcase,
  Globe,
  HelpCircle,
  PhoneCall
} from 'react-feather';
import ContactInfoDialog from './ContactInfoDialog';
import { Button } from './ui/button';

type AuthShellAction = 'register' | 'signin';

interface AuthShellProps {
  children: React.ReactNode;
  action?: AuthShellAction;
  contentClassName?: string;
  footer?: React.ReactNode;
  helpLabel?: React.ReactNode;
  mainClassName?: string;
  onHelp?: () => void;
  showLanguageToggle?: boolean;
}

// Shared chrome for public authentication pages.
export const AuthShell: React.FC<AuthShellProps> = ({
  children,
  action,
  contentClassName = 'mx-auto grid w-full max-w-7xl items-center gap-8 px-4 py-10 sm:px-6 lg:px-8 lg:py-16',
  footer,
  helpLabel,
  mainClassName = 'surface-grid flex flex-1',
  onHelp,
  showLanguageToggle = false
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showContactInfoDialog, setShowContactInfoDialog] = useState(false);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('userLanguage', lng);
  };

  const languageButtonClass = (lng: string) =>
    i18n.language === lng
      ? 'bg-secondary text-secondary-foreground'
      : 'text-primary-foreground/80 hover:bg-white/10 hover:text-primary-foreground';

  const renderPrimaryAction = () => {
    if (action === 'register') {
      return (
        <Button
          id="register-link"
          type="button"
          variant="outline"
          className="login-register-button border-white/30 bg-transparent text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
          onClick={() => navigate('/register')}
        >
          <Briefcase className="h-4 w-4" aria-hidden="true" />
          <span className="login-action-label login-action-label--full">{t('registerCompany')}</span>
          <span className="login-action-label login-action-label--compact">{t('registerCompanyShort')}</span>
        </Button>
      );
    }

    if (action === 'signin') {
      return (
        <Button
          type="button"
          variant="outline"
          className="login-register-button border-white/30 bg-transparent text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
          onClick={() => navigate('/login')}
        >
          <span className="login-action-label">{t('signInLink')}</span>
        </Button>
      );
    }

    return null;
  };

  return (
    <div className="login-shell flex min-h-screen flex-col bg-background text-foreground">
      <header className="login-header sticky top-0 z-20 border-b border-white/10 bg-primary text-primary-foreground shadow-sm">
        <div className="login-header-inner mx-auto flex min-h-16 w-full max-w-7xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="login-brand flex min-w-0 items-center gap-3 text-left"
          >
            <span className="login-brand-text truncate text-base font-semibold">{t('appName')}</span>
          </button>

          <nav className="login-header-nav ml-auto flex flex-wrap items-center justify-end gap-2">
            {showLanguageToggle && (
              <div className="login-language-toggle flex items-center rounded-md border border-white/20 bg-white/5 p-1">
                <Globe className="ml-2 h-4 w-4 text-primary-foreground/70" aria-hidden="true" />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="Switch language to Chinese"
                  className={languageButtonClass('zh')}
                  onClick={() => changeLanguage('zh')}
                >
                  <span className="login-language-label">中文</span>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="Switch language to English"
                  className={languageButtonClass('en')}
                  onClick={() => changeLanguage('en')}
                >
                  <span className="login-language-label login-language-label--full">English</span>
                  <span className="login-language-label login-language-label--compact">EN</span>
                </Button>
              </div>
            )}

            {onHelp && (
              <Button
                id="help-button"
                type="button"
                variant="ghost"
                className="login-header-link text-primary-foreground/80 hover:bg-white/10 hover:text-primary-foreground"
                onClick={onHelp}
                aria-label={typeof helpLabel === 'string' ? helpLabel : t('help')}
              >
                <HelpCircle className="h-4 w-4" aria-hidden="true" />
                <span className="login-action-label login-action-label--hide-compact">{helpLabel ?? t('help')}</span>
              </Button>
            )}

            <Button
              type="button"
              variant="ghost"
              className="login-header-link text-primary-foreground/80 hover:bg-white/10 hover:text-primary-foreground"
              onClick={() => setShowContactInfoDialog(true)}
              aria-label={t('contact')}
            >
              <PhoneCall className="h-4 w-4" aria-hidden="true" />
              <span className="login-action-label login-action-label--hide-compact">{t('contact')}</span>
            </Button>

            {renderPrimaryAction()}
          </nav>
        </div>
      </header>

      <main className={mainClassName}>
        <div className={contentClassName}>
          {children}
        </div>
      </main>

      {footer}

      <ContactInfoDialog
        open={showContactInfoDialog}
        onClose={() => setShowContactInfoDialog(false)}
      />
    </div>
  );
};

export default AuthShell;
